import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { Retry } from '@src/components/common';
import React, { ReactNode, useState } from 'react';
import { useFetchAlerts } from '../hooks/useFetchAlerts';
import {
  CollapseWrapper,
  Navig,
  StyledContent,
  StyledLayout,
  StyledMenu,
  StyledNoAlert,
  StyledSider,
  StyledStatusText,
  StyledSubText,
  TriggerButton,
} from './styled';
import TableContainer from './TableContainer';
import { searchTextAtom, tableFiltersAtom } from '../atoms';
import { useAtom, useAtomValue } from 'jotai';
import { getSidebarConfig, STATIC_SIDEBAR_FILTERS } from './utils';
import { useSelector } from 'react-redux';
import { featureSelector } from '@src/store/common/selectors';
import useAiAlertStore from '@src/globalStore/aiAlertStore';

const ContentPanel = () => {
  const [filterItems, setFilterItems] = useAtom(tableFiltersAtom);
  const { interval } = useAiAlertStore((state) => ({
    interval: state.interval,
  }));
  const searchText = useAtomValue(searchTextAtom);
  const { alerts, isFetching, isError, refetch } = useFetchAlerts(interval);
  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapsed = () => setCollapsed(!collapsed);
  const isAdAlertEnabled = useSelector(featureSelector('ad_ai_alerts'));
  const featureFlags = {
    isAdAlertEnabled,
  };
  const { sidebarItems, hasNoValidMetricAlerts, defaultOpenCategories } = getSidebarConfig({
    alerts,
    featureFlags,
  });
  const { ALL_ALERTS } = STATIC_SIDEBAR_FILTERS;

  const onSelectMenuItem = ({ key, selectedKeys }) => {
    if (key === ALL_ALERTS) {
      setFilterItems([key]);
    } else setFilterItems(selectedKeys.filter((key) => key !== ALL_ALERTS));
  };

  const onDeselectMenuItem = ({ selectedKeys }) => setFilterItems(selectedKeys);

  const getEmpty = (): ReactNode => {
    if (hasNoValidMetricAlerts && !isFetching && !isError) {
      if (searchText) {
        return (
          <StyledNoAlert>
            <StyledSubText>No alerts found.</StyledSubText>
          </StyledNoAlert>
        );
      } else {
        return (
          <StyledNoAlert>
            <StyledStatusText>All metrics are in green!</StyledStatusText>

            <StyledSubText>No alerts detected.</StyledSubText>
          </StyledNoAlert>
        );
      }
    } else return null;
  };

  return (
    <Retry auto={false} isLoading={isFetching} showError={isError} fetchData={refetch} progress>
      <StyledLayout>
        <StyledSider
          width="16%"
          collapsible
          collapsed={collapsed}
          onCollapse={toggleCollapsed}
          collapsedWidth={0}
          theme="light"
          trigger={null}
        >
          <CollapseWrapper>
            <Navig collapsed={collapsed}></Navig>
            <TriggerButton collapsed={collapsed} onClick={toggleCollapsed}>
              {collapsed ? <RightOutlined /> : <LeftOutlined />}
            </TriggerButton>
          </CollapseWrapper>

          <StyledMenu
            mode="inline"
            theme="light"
            inlineCollapsed={collapsed}
            multiple
            items={sidebarItems}
            inlineIndent={8}
            selectedKeys={filterItems}
            defaultOpenKeys={defaultOpenCategories}
            onSelect={onSelectMenuItem}
            onDeselect={onDeselectMenuItem}
          />
          {getEmpty()}
        </StyledSider>
        <StyledContent collapsed={collapsed}>
          <TableContainer alerts={alerts} interval={interval} />
        </StyledContent>
      </StyledLayout>
    </Retry>
  );
};

export default ContentPanel;
